import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Edit, Trash2, Plus, Target, AlertTriangle, TrendingUp, DollarSign, Save, Loader2, FileText, Beaker, Briefcase, FolderKanban, Lock, ListTodo } from "lucide-react";
import { InitiativeApprovalsCard } from "../components/approvals/InitiativeApprovalsCard";
import { InitiativeTrackingTab } from "../components/portfolio/InitiativeTrackingTab";
import { InitiativeSuppliersTab } from "../components/portfolio/InitiativeSuppliersTab";
import { InitiativeTasksTab } from "../components/tasks/InitiativeTasksTab";
import { EsgTagsPicker } from "../components/portfolio/EsgTagsPicker";
import { InitiativeDocumentsCard } from "../components/portfolio/InitiativeDocumentsCard";

const STATUS_LABELS = {
  idea: "Idea",
  backlog: "Backlog",
  evaluation: "En Evaluación",
  validation: "En Validación",
  in_progress: "En Progreso",
  pilot: "Piloto",
  scaling: "Escalando",
  completed: "Completado",
  cancelled: "Cancelado",
};

const TYPE_LABELS = {
  digital: "Digital",
  open_innovation: "Open Innovation",
  rd: "I+D",
  pilot: "Piloto",
  prop: "PROP (propuesta)",
};

function InitiativeDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  
  const [initiative, setInitiative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [kpis, setKpis] = useState([]);
  const [risks, setRisks] = useState([]);
  const [editData, setEditData] = useState({});
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newKpi, setNewKpi] = useState({ name: "", target_type: "numeric", target_value: "", target_date: "", current_value: "", unit: "" });
  const [newRisk, setNewRisk] = useState({ description: "", severity: 3, probability: 3, mitigation: "" });

  const fetchData = useCallback(async () => {
    if (!params.id || !auth.token) return;
    try {
      setLoading(true);
      const data = await api.getInitiative(auth.token, params.id);
      setInitiative(data);
      setEditData(data);
      const kpisData = await api.getKPIs(auth.token, params.id).catch(() => []);
      const risksData = await api.getRisks(auth.token, params.id).catch(() => []);
      setKpis(kpisData);
      setRisks(risksData);
    } catch (err) {
      toast.error("Error al cargar la iniciativa");
      navigate("/portfolio");
    } finally {
      setLoading(false);
    }
  }, [params.id, auth.token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateInitiative(auth.token, params.id, editData);
      setInitiative(editData);
      setEditMode(false);
      toast.success("Iniciativa actualizada");
    } catch (err) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteInitiative(auth.token, params.id);
      toast.success("Iniciativa eliminada");
      navigate("/portfolio");
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleAddKpi = async () => {
    try {
      await api.createKPI(auth.token, {
        initiative_id: params.id,
        name: newKpi.name,
        target_type: newKpi.target_type,
        target_value: newKpi.target_type === "numeric" && newKpi.target_value ? parseFloat(newKpi.target_value) : null,
        target_date: newKpi.target_type === "date" && newKpi.target_date ? newKpi.target_date : null,
        current_value: newKpi.current_value ? parseFloat(newKpi.current_value) : null,
        unit: newKpi.unit || null,
      });
      toast.success("KPI añadido");
      setKpiDialogOpen(false);
      setNewKpi({ name: "", target_type: "numeric", target_value: "", target_date: "", current_value: "", unit: "" });
      const updatedKpis = await api.getKPIs(auth.token, params.id);
      setKpis(updatedKpis);
    } catch (err) {
      toast.error("Error al añadir KPI");
    }
  };

  const handleDeleteKpi = async (kpiId) => {
    try {
      await api.deleteKPI(auth.token, kpiId);
      toast.success("KPI eliminado");
      setKpis((prev) => prev.filter((k) => k.id !== kpiId));
    } catch (err) {
      toast.error("Error al eliminar KPI");
    }
  };

  const handleAddRisk = async () => {
    try {
      await api.createRisk(auth.token, {
        initiative_id: params.id,
        description: newRisk.description,
        severity: newRisk.severity,
        probability: newRisk.probability,
        mitigation: newRisk.mitigation || null,
      });
      toast.success("Riesgo añadido");
      setRiskDialogOpen(false);
      setNewRisk({ description: "", severity: 3, probability: 3, mitigation: "" });
      const updatedRisks = await api.getRisks(auth.token, params.id);
      setRisks(updatedRisks);
    } catch (err) {
      toast.error("Error al añadir riesgo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!initiative) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-muted-foreground">Iniciativa no encontrada</p>
        <Button onClick={() => navigate("/portfolio")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Portfolio
        </Button>
      </div>
    );
  }

  const trlProgress = initiative.trl_initial && initiative.trl_target 
    ? Math.round((initiative.trl_initial / initiative.trl_target) * 100)
    : 0;

  const getTypeIcon = () => {
    if (initiative.type === "pilot") return <Beaker className="w-6 h-6 text-primary" />;
    if (initiative.type === "rd") return <FolderKanban className="w-6 h-6 text-primary" />;
    return <Briefcase className="w-6 h-6 text-primary" />;
  };

  return (
    <div className="space-y-6 animate-in" data-testid="initiative-detail-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/portfolio")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                {getTypeIcon()}
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="initiative-title">
                  {initiative.name}
                </h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="outline">{TYPE_LABELS[initiative.type] || initiative.type}</Badge>
                  <span className={"status-badge status-" + (initiative.status || "").replace("_", "-")}>
                    {STATUS_LABELS[initiative.status] || initiative.status}
                  </span>
                  {initiative.status_locked && (
                    <span className="text-[10px] text-amber-600 flex items-center gap-0.5" title="Estado fijado a mano" data-testid="status-locked-view-badge">
                      <Lock className="w-3 h-3" />bloqueado
                    </span>
                  )}
                  {initiative.innohub_path && (
                    <a href={initiative.innohub_path.startsWith("http") ? initiative.innohub_path : undefined} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5" data-testid="innohub-path-link">
                      <FolderKanban className="w-3 h-3" />InnoHub
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-btn">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="destructive" data-testid="delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>¿Eliminar iniciativa?</DialogTitle>
                    <DialogDescription>
                      Esta acción no se puede deshacer.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Eliminar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {auth.user?.role === "admin" && (initiative.name || "").toLowerCase().includes("barad") && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300"
          data-testid="barad-dur-policy-note"
        >
          <b>Política de esta iniciativa (Barad-dûr / Palantir-Foundry):</b> transformación digital
          interna, incluida en Portfolio, PG-07, Gantt, Cross-Link y búsqueda — pero excluida de los
          totales financieros de los 27/28 proyectos I+D RD (no forma parte de ese censo).
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">TRL</p>
                {initiative.trl_initial === 4 && initiative.trl_target === 7 && !initiative.trl_verified ? (
                  <p className="text-xl font-bold text-muted-foreground italic" title="4→7 es el valor por defecto asignado en la migración masiva; pendiente de evaluación TRL real por el equipo técnico." data-testid="trl-unverified-badge">
                    Sin TRL
                  </p>
                ) : (
                  <p className="text-xl font-bold">{initiative.trl_initial || "?"} → {initiative.trl_target || "?"}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Presupuesto</p>
                <p className="text-xl font-bold">{initiative.budget_estimated ? (initiative.budget_estimated / 1000).toFixed(0) + "K€" : "N/A"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">KPIs</p>
                <p className="text-xl font-bold">{kpis.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Riesgos</p>
                <p className="text-xl font-bold">{risks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <FileText className="w-4 h-4 mr-2" />General
          </TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking">
            <FolderKanban className="w-4 h-4 mr-2" />Seguimiento
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">
            <Briefcase className="w-4 h-4 mr-2" />Proveedores
          </TabsTrigger>
          <TabsTrigger value="kpis" data-testid="tab-kpis">
            <Target className="w-4 h-4 mr-2" />KPIs
          </TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks">
            <AlertTriangle className="w-4 h-4 mr-2" />Riesgos
          </TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">
            <ListTodo className="w-4 h-4 mr-2" />Tareas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Información General</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <>
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input value={editData.name || ""} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea value={editData.description || ""} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={4} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo</Label>
                        <Select value={editData.type} onValueChange={(value) => setEditData({ ...editData, type: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="digital">Digital</SelectItem>
                            <SelectItem value="open_innovation">Open Innovation</SelectItem>
                            <SelectItem value="rd">I+D</SelectItem>
                            <SelectItem value="pilot">Piloto</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1.5">
                          Estado
                          {editData.status_locked && (
                            <span className="text-[10px] font-normal text-amber-600 flex items-center gap-0.5" data-testid="status-locked-badge" title="Fijado a mano — Airtable no lo sobrescribirá en el próximo sync">
                              <Lock className="w-3 h-3" />bloqueado
                            </span>
                          )}
                        </Label>
                        <Select value={editData.status} onValueChange={(value) => setEditData({ ...editData, status: value })} data-testid="edit-status-select">
                          <SelectTrigger data-testid="edit-status-trigger"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="idea">Idea</SelectItem>
                            <SelectItem value="backlog">Backlog</SelectItem>
                            <SelectItem value="in_progress">En Progreso</SelectItem>
                            <SelectItem value="pilot">Piloto</SelectItem>
                            <SelectItem value="scaling">Escalando</SelectItem>
                            <SelectItem value="completed">Completado</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                          </SelectContent>
                        </Select>
                        {editData.status_locked && (
                          <button
                            type="button"
                            className="text-[11px] text-muted-foreground underline hover:text-foreground"
                            onClick={() => setEditData({ ...editData, status_locked: false })}
                            data-testid="unlock-status-btn"
                          >
                            Desbloquear (volver a dejarlo en manos de Airtable)
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Carpeta InnoHub (ruta o enlace)</Label>
                      <Input
                        value={editData.innohub_path || ""}
                        onChange={(e) => setEditData({ ...editData, innohub_path: e.target.value })}
                        placeholder="https://valorizasm.sharepoint.com/sites/InnoHub/... o ruta de carpeta"
                        data-testid="edit-innohub-path"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Prioridad</Label>
                        <Select value={String(editData.priority || 3)} onValueChange={(value) => setEditData({ ...editData, priority: parseInt(value) })}>
                          <SelectTrigger data-testid="edit-priority"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 - Baja</SelectItem>
                            <SelectItem value="2">2</SelectItem>
                            <SelectItem value="3">3 - Media</SelectItem>
                            <SelectItem value="4">4</SelectItem>
                            <SelectItem value="5">5 - Crítica</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>TRL Inicial</Label>
                        <Select value={String(editData.trl_initial || 1)} onValueChange={(value) => setEditData({ ...editData, trl_initial: parseInt(value), trl_verified: true })}>
                          <SelectTrigger data-testid="edit-trl-initial"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9].map(t => <SelectItem key={t} value={String(t)}>TRL {t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>TRL Objetivo</Label>
                        <Select value={String(editData.trl_target || 9)} onValueChange={(value) => setEditData({ ...editData, trl_target: parseInt(value), trl_verified: true })}>
                          <SelectTrigger data-testid="edit-trl-target"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9].map(t => <SelectItem key={t} value={String(t)}>TRL {t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">Al tocar el TRL se marca como verificado por el equipo (quita el aviso "sin verificar").</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Área</Label>
                      <Input 
                        value={editData.area || ""} 
                        onChange={(e) => setEditData({ ...editData, area: e.target.value })} 
                        placeholder="Ej: Tratamiento Residuos, Smart City..."
                        data-testid="edit-area"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoría Sostenible</Label>
                      <Input
                        value={editData.sustainable_category || ""}
                        onChange={(e) => setEditData({ ...editData, sustainable_category: e.target.value })}
                        placeholder="Ej: Economía circular, Descarbonización..."
                        data-testid="edit-sustainable-category"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tecnología</Label>
                      <Input
                        value={editData.technology || ""}
                        onChange={(e) => setEditData({ ...editData, technology: e.target.value })}
                        placeholder="Ej: IA, IoT, Visión artificial..."
                        data-testid="edit-technology"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tags ESG</Label>
                      <EsgTagsPicker
                        token={auth.token}
                        selected={editData.esg_tags || []}
                        onChange={(tags) => setEditData({ ...editData, esg_tags: tags })}
                      />
                    </div>

                    <div className="pt-2 border-t space-y-3">
                      <h4 className="text-sm font-semibold">Equipo</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Sponsor(s) (separados por coma)</Label>
                          <Input
                            value={(editData.sponsors && editData.sponsors.length ? editData.sponsors : (editData.sponsor ? [editData.sponsor] : [])).join(", ")}
                            onChange={(e) => {
                              const list = e.target.value.split(",").map((t) => t.trim()).filter(Boolean);
                              setEditData({ ...editData, sponsors: list, sponsor: list[0] || "" });
                            }}
                            data-testid="edit-sponsors"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Project Manager</Label>
                          <Input
                            value={editData.project_manager || ""}
                            onChange={(e) => setEditData({ ...editData, project_manager: e.target.value })}
                            data-testid="edit-project-manager"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t space-y-3">
                      <h4 className="text-sm font-semibold">Socios</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Socio / Partner</Label>
                          <Input
                            value={editData.partner || ""}
                            onChange={(e) => setEditData({ ...editData, partner: e.target.value })}
                            data-testid="edit-partner"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tipo de Socio (separados por coma)</Label>
                          <Input
                            value={(editData.partner_type || []).join(", ")}
                            onChange={(e) => setEditData({ ...editData, partner_type: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                            data-testid="edit-partner-type"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t space-y-3">
                      <h4 className="text-sm font-semibold">Riesgos</h4>
                      <div className="space-y-2">
                        <Label>Riesgo</Label>
                        <Textarea
                          rows={2}
                          value={editData.risk || ""}
                          onChange={(e) => setEditData({ ...editData, risk: e.target.value })}
                          data-testid="edit-risk"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Dificultades encontradas</Label>
                        <Textarea
                          rows={2}
                          value={editData.difficulties || ""}
                          onChange={(e) => setEditData({ ...editData, difficulties: e.target.value })}
                          data-testid="edit-difficulties"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Potencial de expansión a otros negocios</Label>
                        <Textarea
                          rows={2}
                          value={editData.expansion_potential || ""}
                          onChange={(e) => setEditData({ ...editData, expansion_potential: e.target.value })}
                          data-testid="edit-expansion-potential"
                        />
                      </div>
                    </div>

                    <div className="pt-2 border-t space-y-3">
                      <h4 className="text-sm font-semibold">Financiación</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Organismo Financiador</Label>
                          <Input
                            value={editData.organismo_financiador || ""}
                            onChange={(e) => setEditData({ ...editData, organismo_financiador: e.target.value })}
                            data-testid="edit-organismo-financiador"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Convocatoria</Label>
                          <Input
                            value={editData.convocatoria || ""}
                            onChange={(e) => setEditData({ ...editData, convocatoria: e.target.value })}
                            data-testid="edit-convocatoria"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Origen de los Fondos</Label>
                          <Input
                            value={editData.origen_fondos || ""}
                            onChange={(e) => setEditData({ ...editData, origen_fondos: e.target.value })}
                            data-testid="edit-origen-fondos"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Fondos Totales Externos (€)</Label>
                          <Input
                            type="number"
                            value={editData.external_funds ?? ""}
                            onChange={(e) => setEditData({ ...editData, external_funds: e.target.value ? parseFloat(e.target.value) : null })}
                            data-testid="edit-external-funds"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Presupuesto Total del proyecto (€)</Label>
                          <Input
                            type="number"
                            value={editData.budget_total ?? ""}
                            onChange={(e) => setEditData({ ...editData, budget_total: e.target.value ? parseFloat(e.target.value) : null })}
                            data-testid="edit-budget-total"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Descripción</h4>
                      <p>{initiative.description || "Sin descripción"}</p>
                    </div>
                    {initiative.hypothesis && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Hipótesis</h4>
                        <p>{initiative.hypothesis}</p>
                      </div>
                    )}
                    {(initiative.sustainable_category || initiative.technology || (initiative.esg_tags || []).length > 0) && (
                      <div className="grid grid-cols-2 gap-3 pt-2" data-testid="initiative-esg-block">
                        {initiative.sustainable_category && (
                          <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Categoría Sostenible</h4>
                            <p className="text-sm">{initiative.sustainable_category}</p>
                          </div>
                        )}
                        {initiative.technology && (
                          <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Tecnología</h4>
                            <p className="text-sm">{initiative.technology}</p>
                          </div>
                        )}
                        {(initiative.esg_tags || []).length > 0 && (
                          <div className="col-span-2">
                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Tags ESG</h4>
                            <div className="flex flex-wrap gap-1">
                              {initiative.esg_tags.map((t, i) => <Badge key={i} variant="secondary">{t}</Badge>)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Progreso TRL</CardTitle>
                  <p className="text-xs text-muted-foreground -mt-1">Madurez tecnológica (TRL) — no es el % de avance del proyecto</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>TRL {initiative.trl_initial || 1}</span>
                      <span>TRL {initiative.trl_target || 9}</span>
                    </div>
                    <Progress value={trlProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground text-center">{trlProgress}% de madurez TRL alcanzada</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Detalles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Prioridad</span>
                    <span className="font-medium">{initiative.priority || "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Área</span>
                    <span>{initiative.area || "Sin asignar"}</span>
                  </div>
                </CardContent>
              </Card>

              <InitiativeDocumentsCard initiative={initiative} token={auth.token} onChange={(docs) => { setInitiative({ ...initiative, documents: docs }); setEditData((d) => ({ ...d, documents: docs })); }} />
            </div>
          </div>
          <InitiativeApprovalsCard initiativeId={initiative.id} token={auth.token} />
        </TabsContent>

        <TabsContent value="tracking" className="space-y-4">
          <InitiativeTrackingTab initiative={initiative} />
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          <InitiativeSuppliersTab initiativeId={initiative.id} token={auth.token} />
        </TabsContent>

        <TabsContent value="kpis" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">KPIs de la Iniciativa</h3>
            <Dialog open={kpiDialogOpen} onOpenChange={setKpiDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-kpi-btn"><Plus className="w-4 h-4 mr-2" />Añadir KPI</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuevo KPI</DialogTitle>
                  <DialogDescription>Añade un nuevo indicador de rendimiento</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre del KPI *</Label>
                    <Input value={newKpi.name} onChange={(e) => setNewKpi({ ...newKpi, name: e.target.value })} placeholder="Ej: Reducción de emisiones CO2" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de objetivo</Label>
                    <Select value={newKpi.target_type} onValueChange={(v) => setNewKpi({ ...newKpi, target_type: v })}>
                      <SelectTrigger data-testid="kpi-target-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="numeric">Numérico</SelectItem>
                        <SelectItem value="date">Fecha</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Valor Objetivo</Label>
                      {newKpi.target_type === "date" ? (
                        <Input type="date" data-testid="kpi-target-date-input" value={newKpi.target_date} onChange={(e) => setNewKpi({ ...newKpi, target_date: e.target.value })} />
                      ) : (
                        <Input type="number" data-testid="kpi-target-value-input" value={newKpi.target_value} onChange={(e) => setNewKpi({ ...newKpi, target_value: e.target.value })} placeholder="100" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Valor Actual</Label>
                      <Input type="number" value={newKpi.current_value} onChange={(e) => setNewKpi({ ...newKpi, current_value: e.target.value })} placeholder="0" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Unidad</Label>
                    <Input value={newKpi.unit} onChange={(e) => setNewKpi({ ...newKpi, unit: e.target.value })} placeholder="%, tons, €" disabled={newKpi.target_type === "date"} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setKpiDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleAddKpi} disabled={!newKpi.name}>Añadir</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {kpis.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground" data-testid="kpis-empty-state">
                <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-4">No hay KPIs definidos para esta iniciativa</p>
                <Button variant="outline" size="sm" onClick={() => setKpiDialogOpen(true)} data-testid="kpis-empty-cta">
                  <Plus className="w-4 h-4 mr-2" />Añadir el primer KPI
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kpis.map((kpi) => (
                <KpiCard key={kpi.id} kpi={kpi} onDelete={handleDeleteKpi} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="risks" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Matriz de Riesgos</h3>
            <Dialog open={riskDialogOpen} onOpenChange={setRiskDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-risk-btn"><Plus className="w-4 h-4 mr-2" />Añadir Riesgo</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuevo Riesgo</DialogTitle>
                  <DialogDescription>Identifica un nuevo riesgo para la iniciativa</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Descripción del Riesgo *</Label>
                    <Textarea value={newRisk.description} onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })} placeholder="Describe el riesgo..." rows={3} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Severidad (1-5)</Label>
                      <Select value={String(newRisk.severity)} onValueChange={(v) => setNewRisk({ ...newRisk, severity: parseInt(v) })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Bajo</SelectItem>
                          <SelectItem value="2">2 - Bajo</SelectItem>
                          <SelectItem value="3">3 - Medio</SelectItem>
                          <SelectItem value="4">4 - Alto</SelectItem>
                          <SelectItem value="5">5 - Alto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Probabilidad (1-5)</Label>
                      <Select value={String(newRisk.probability)} onValueChange={(v) => setNewRisk({ ...newRisk, probability: parseInt(v) })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Baja</SelectItem>
                          <SelectItem value="2">2 - Baja</SelectItem>
                          <SelectItem value="3">3 - Media</SelectItem>
                          <SelectItem value="4">4 - Alta</SelectItem>
                          <SelectItem value="5">5 - Alta</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Plan de Mitigación</Label>
                    <Textarea value={newRisk.mitigation} onChange={(e) => setNewRisk({ ...newRisk, mitigation: e.target.value })} placeholder="¿Cómo se mitigará?" rows={2} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRiskDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleAddRisk} disabled={!newRisk.description}>Añadir</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {risks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground" data-testid="risks-empty-state">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-4">No hay riesgos identificados todavía</p>
                <Button variant="outline" size="sm" onClick={() => setRiskDialogOpen(true)} data-testid="risks-empty-cta">
                  <Plus className="w-4 h-4 mr-2" />Identificar el primer riesgo
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {risks.map((risk) => (
                <RiskCard key={risk.id} risk={risk} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <InitiativeTasksTab initiativeId={initiative.id} initiativeName={initiative.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ kpi, onDelete }) {
  if (kpi.target_type === "date" && kpi.target_date) {
    const isPast = new Date(kpi.target_date) < new Date();
    return (
      <Card data-testid={`kpi-card-${kpi.id}`}>
        <CardHeader className="pb-2 flex-row items-start justify-between space-y-0">
          <CardTitle className="text-base">{kpi.name}</CardTitle>
          <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1 -mr-1" onClick={() => onDelete?.(kpi.id)} data-testid={`kpi-delete-${kpi.id}`}>
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold">{kpi.target_date}</span>
            <Badge variant={isPast ? "secondary" : "outline"}>{isPast ? "Fecha pasada" : "Objetivo fecha"}</Badge>
          </div>
        </CardContent>
      </Card>
    );
  }
  const progress = kpi.target_value ? Math.round((kpi.current_value / kpi.target_value) * 100) : 0;
  return (
    <Card data-testid={`kpi-card-${kpi.id}`}>
      <CardHeader className="pb-2 flex-row items-start justify-between space-y-0">
        <CardTitle className="text-base">{kpi.name}</CardTitle>
        <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1 -mr-1" onClick={() => onDelete?.(kpi.id)} data-testid={`kpi-delete-${kpi.id}`}>
          <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex justify-between items-end">
            <span className="text-3xl font-bold">{kpi.current_value ?? 0}</span>
            <span className="text-muted-foreground">/ {kpi.target_value ?? "?"} {kpi.unit || ""}</span>
          </div>
          <Progress value={Math.min(progress, 100)} className="h-2" />
          <p className="text-xs text-muted-foreground">{progress}% del objetivo</p>
        </div>
      </CardContent>
    </Card>
  );
}

function RiskCard({ risk }) {
  const score = risk.severity * risk.probability;
  const scoreColor = score >= 15 ? "text-red-500" : score >= 8 ? "text-amber-500" : "text-green-500";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="space-y-3">
          <p className="font-medium">{risk.description}</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Severidad:</span>
              <Badge variant={risk.severity >= 4 ? "destructive" : "secondary"}>{risk.severity}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Prob:</span>
              <Badge variant={risk.probability >= 4 ? "destructive" : "secondary"}>{risk.probability}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Score:</span>
              <span className={"font-bold " + scoreColor}>{score}</span>
            </div>
          </div>
          {risk.mitigation && (
            <div>
              <span className="text-sm text-muted-foreground">Mitigación: </span>
              <span className="text-sm">{risk.mitigation}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default InitiativeDetailPage;
